import React, { useEffect, useMemo, useState } from 'react'
import {
    Grid,
    TextField,
    MenuItem,
} from '@mui/material';
import SectionTitle from '../../../ui/sectionTitle';
import { ValidDataService } from '../../../../service/validDataService';

const GENDER_OPTIONS = [
    { value: '', label: 'Select Gender' },
    { value: 'male', label: 'Men' },
    { value: 'female', label: 'Women' },
    { value: 'unisex', label: 'Unisex' },
    { value: 'kids', label: 'Kids' },
];

const RIM_TYPE_OPTIONS = [
    { value: '', label: 'Select Rim Type' },
    { value: 'full_rim', label: 'Full Rim' },
    { value: 'semi_rimless', label: 'Semi Rimless' },
    { value: 'rimless', label: 'Rimless' },
    { value: 'drill_mount', label: 'Drill Mount' },
    { value: 'nylor', label: 'Nylor' },
];

const FRAME_TYPE_OPTIONS = [
    { value: '', label: 'Select Type' },
    { value: 'optical', label: 'Optical' },
    { value: 'sunglasses', label: 'Sunglasses' },
    { value: 'other', label: 'Other' },
];

function ValidFramePropertiesForm({ formik }) {
    const [shapeOptions, setShapeOptions] = useState([]);

    useEffect(() => {
        let isMounted = true;

        const loadShapes = async () => {
            try {
                const response = await ValidDataService.getFrameShapesFilter();
                if (!isMounted || response.code !== 0 || !Array.isArray(response.data)) {
                    return;
                }

                setShapeOptions(response.data.map((shape) => ({
                    value: shape.name,
                    label: shape.name,
                })));
            } catch (error) {
                console.error('Failed to load frame shapes', error);
            }
        };

        loadShapes();

        return () => {
            isMounted = false;
        };
    }, []);

    const resolvedShapeOptions = useMemo(() => {
        const currentShape = formik.values.properties.shape;
        if (!currentShape || shapeOptions.some((option) => option.value === currentShape)) {
            return shapeOptions;
        }

        return [{ value: currentShape, label: currentShape }, ...shapeOptions];
    }, [shapeOptions, formik.values.properties.shape]);

    return (
        <>
            {/* Section 3: Frame Properties */}
            <Grid item xs={12}>
                <SectionTitle title="Frame Properties" />
            </Grid>

            <Grid container spacing={2}>
                {/* Section 3a: Measurements */}
                <Grid item xs={12} sm={6}>
                    <Grid item xs={12} mt={2}>
                        <TextField
                            value={formik.values.properties.aMeasurement}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.aMeasurement`, event.target.value);
                              }}
                            name="aMeasurement"
                            label="A (Eye Size)"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.aMeasurement && Boolean(formik.errors.aMeasurement)}
                            helperText={formik.touched.aMeasurement && formik.errors.aMeasurement}
                        />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.bMeasurement}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.bMeasurement`, event.target.value);
                              }}
                            name="bMeasurement"
                            label="B (Lens Height)"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.bMeasurement && Boolean(formik.errors.bMeasurement)}
                            helperText={formik.touched.bMeasurement && formik.errors.bMeasurement}
                        />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.dblMeasurement}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.dblMeasurement`, event.target.value);
                              }}
                            name="dblMeasurement"
                            label="DBL (Bridge)"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.dblMeasurement && Boolean(formik.errors.dblMeasurement)}
                            helperText={formik.touched.dblMeasurement && formik.errors.dblMeasurement}
                        />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.templeLength}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.templeLength`, event.target.value);
                              }}
                            name="templeLength"
                            label="Temple Length"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.templeLength && Boolean(formik.errors.templeLength)}
                            helperText={formik.touched.templeLength && formik.errors.templeLength}
                        />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.edMeasurement}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.edMeasurement`, event.target.value);
                              }}
                            name="edMeasurement"
                            label="ED (Effective Diameter)"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.edMeasurement && Boolean(formik.errors.edMeasurement)}
                            helperText={formik.touched.edMeasurement && formik.errors.edMeasurement}
                        />
                    </Grid>
                    {/* <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.circumference}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.circumference`, event.target.value);
                              }}
                            name="circumference"
                            label="Circumference"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.circumference && Boolean(formik.errors.circumference)}
                            helperText={formik.touched.circumference && formik.errors.circumference}
                        />
                    </Grid> */}
                </Grid>

                {/* Section 3b: Properties and Colors */}
                <Grid item xs={12} sm={6}>
                    <Grid item xs={12} mt={2}>
                        <TextField
                                                        select
                            value={formik.values.properties.gender}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.gender`, event.target.value);
                              }}
                            name="gender"
                            label="Gender"
                            size='small'
                            fullWidth
                            error={formik.touched.gender && Boolean(formik.errors.gender)}
                            helperText={formik.touched.gender && formik.errors.gender}
                        >
                            {GENDER_OPTIONS.map((option) => (
                                <MenuItem key={option.value || 'empty'} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                            select
                            value={formik.values.properties.frameType}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.frameType`, event.target.value);
                              }}
                            name="frameType"
                            label="Type"
                            size='small'
                            fullWidth
                            error={formik.touched.frameType && Boolean(formik.errors.frameType)}
                            helperText={formik.touched.frameType && formik.errors.frameType}
                        >
                            {FRAME_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.value || 'empty'} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                                                        select
                            value={formik.values.properties.rimType}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.rimType`, event.target.value);
                              }}
                            name="rimType"
                            label="Rim Type"
                            size='small'
                            fullWidth
                            error={formik.touched.rimType && Boolean(formik.errors.rimType)}
                            helperText={formik.touched.rimType && formik.errors.rimType}
                        >
                            {RIM_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.value || 'empty'} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                            select
                            value={formik.values.properties.shape}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.shape`, event.target.value);
                              }}
                            name="shape"
                            label="Shape"
                            size='small'
                            fullWidth
                            error={formik.touched.shape && Boolean(formik.errors.shape)}
                            helperText={formik.touched.shape && formik.errors.shape}
                        >
                            <MenuItem value="">Select Shape</MenuItem>
                            {resolvedShapeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.color}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.color`, event.target.value);
                              }}
                            name="color"
                            label="Frame Color"
                            size='small'
                            fullWidth
                            error={formik.touched.color && Boolean(formik.errors.color)}
                            helperText={formik.touched.color && formik.errors.color}
                        />
                    </Grid>
                    {/* <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.lensColor}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.lensColor`, event.target.value);
                              }}
                            name="lensColor"
                            label="Lens Color"
                            size='small'
                            fullWidth
                            error={formik.touched.lensColor && Boolean(formik.errors.lensColor)}
                            helperText={formik.touched.lensColor && formik.errors.lensColor}
                        />
                    </Grid> */}
                </Grid>

            </Grid>
        </>
    )
}

export default ValidFramePropertiesForm
